class ColorFormatter {
  static rgbToHsl(red, green, blue) {
    red /= 255;
    green /= 255;
    blue /= 255;

    const maximumValue = Math.max(red, green, blue);
    const minimumValue = Math.min(red, green, blue);
    let hue, saturation, lightness = (maximumValue + minimumValue) / 2;

    if (maximumValue === minimumValue) {
      hue = saturation = 0;
    } else {
      const difference = maximumValue - minimumValue;
      saturation = lightness > 0.5 ? difference / (2 - maximumValue - minimumValue) : difference / (maximumValue + minimumValue);

      switch (maximumValue) {
        case red:
          hue = (green - blue) / difference + (green < blue ? 6 : 0);
          break;
        case green:
          hue = (blue - red) / difference + 2;
          break;
        case blue:
          hue = (red - green) / difference + 4;
          break;
      }
      hue /= 6;
    }
    return [hue * 360, saturation * 100, lightness * 100];
  }

  static hslToRgb(hue, saturation, lightness) {
    let red, green, blue;

    hue /= 360;
    saturation /= 100;
    lightness /= 100;

    if (saturation === 0) {
      red = green = blue = lightness;
    } else {
      const q = lightness < 0.5 ? lightness * (1 + saturation) : lightness + saturation - lightness * saturation;
      const p = 2 * lightness - q;
      red = this.hueToRgb(p, q, hue + 1 / 3);
      green = this.hueToRgb(p, q, hue);
      blue = this.hueToRgb(p, q, hue - 1 / 3);
    }
    return [Math.round(red * 255), Math.round(green * 255), Math.round(blue * 255)];
  }

  static hueToRgb(p, q, t) {
    if (t < 0) t += 1;
    if (t > 1) t -= 1;

    if (t < 1 / 6) return p + (q - p) * 6 * t;
    if (t < 1 / 2) return q;
    if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
    return p;
  }

  static adjustColor(color) {
    const rgbaMatch = color.match(/rgba?\((\d+),\s*(\d+),\s*(\d+),?\s*(\d*\.?\d+)?\)/);

    const hexMatch = color.match(/^#([a-fA-F0-9]{6})$/);
    if (hexMatch) {
      const hex = hexMatch[1];
      let red = parseInt(hex.substring(0, 2), 16);
      let green = parseInt(hex.substring(2, 4), 16);
      let blue = parseInt(hex.substring(4, 6), 16);
      const alpha = 1;

      return this.processRgbValues(red, green, blue, alpha);
    }

    if (rgbaMatch) {
      let red = parseInt(rgbaMatch[1]);
      let green = parseInt(rgbaMatch[2]);
      let blue = parseInt(rgbaMatch[3]);
      const alpha = parseFloat(rgbaMatch[4]) || 1;

      return this.processRgbValues(red, green, blue, alpha);
    }
    return color;
  }

  static processRgbValues(red, green, blue, alpha) {
    let [hue, saturation, lightness] = this.rgbToHsl(red, green, blue);

    saturation = saturation / 0.8;
    lightness = lightness * 0.4;

    const [newRed, newGreen, newBlue] = this.hslToRgb(hue, saturation, lightness);

    return `rgba(${newRed}, ${newGreen}, ${newBlue}, ${alpha})`;
  }

  static isColorLight(color) {
    const hex = color.replace('#', '');
    const r = parseInt(hex.substring(0, 2), 16);
    const g = parseInt(hex.substring(2, 4), 16);
    const b = parseInt(hex.substring(4, 6), 16);
    const brightness = (r * 299 + g * 587 + b * 114) / 1000;
    return brightness > 155;
  }

  static getContrastingTextColor(hexColor) {
    const hex = hexColor.replace('#', '');
    const r = parseInt(hex.substring(0, 2), 16);
    const g = parseInt(hex.substring(2, 4), 16);
    const b = parseInt(hex.substring(4, 6), 16);
    let [h, s, l] = this.rgbToHsl(r, g, b);

    if (this.isColorLight(hexColor)) {
      s = Math.min(100, s * 1.3);
      l = 25;
    } else {
      s = Math.min(100, s * 0.6);
      l = 90;
    }

    const [nr, ng, nb] = this.hslToRgb(h, s, l);
    return `#${nr.toString(16).padStart(2, '0')}${ng.toString(16).padStart(2, '0')}${nb.toString(16).padStart(2, '0')}`;
  }
}

export default ColorFormatter;
